// ─────────────────────────────────────────────
//  Auth Context — provides user + role to entire app
//
//  Beyond the signed-in profile, this is where the four access tiers
//  (Administrator → AIC → Supervising Doctor → Clinical Staff) are turned into
//  the permission flags the UI gates on, and where the admin-only "View as"
//  testing mode lives. See src/config/roles.js for the tier definitions.
// ─────────────────────────────────────────────
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { onAuth } from "../firebase/auth";
import {
  createUser, listenToDailyBoard, listenToUser, getAssignmentsOnce, updateUser,
  touchPresence,
} from "../firebase/firestore";
import { useDayKey } from "../hooks/useDayKey";
import { resolveLoginFacility } from "../domain/loginFacility";
import { myRoomAssignments } from "../domain/myRooms";
import { HEARTBEAT_MS } from "../domain/presence";
import {
  buildDefaultProfile,
  buildSandboxProfile,
  accessLevelOf,
  ACCESS_LEVELS,
  viewAsProfile,
} from "../config/roles";
import { isSandboxBoot } from "../sandbox/mode";

const AuthContext = createContext(null);

// How long to wait for Firebase's first auth event before assuming it is not
// coming. Generous enough for a cold start on a slow connection, short enough
// that nobody is left staring at a splash screen.
const AUTH_WATCHDOG_MS = 15000;

// "View as" survives a page refresh within the same tab so an admin doesn't
// drop out of preview on reload. It is always re-validated against the real
// profile before being applied, so it can never elevate a non-admin.
const VIEW_AS_KEY = "atc.viewAs";
// The stored "View as" target is either a tier/test-user key (string) or a
// specific-user preview profile (object), serialized as JSON so both round-trip.
const readStoredViewAs = () => {
  try {
    const raw = sessionStorage.getItem(VIEW_AS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

// Testing mode persists per device (localStorage) so an admin's test bench
// stays in testing mode across sessions. Only honoured for a real admin.
const TESTING_MODE_KEY = "atc.testingMode";
const readStoredTestingMode = () => {
  try { return localStorage.getItem(TESTING_MODE_KEY) === "1"; }
  catch { return false; }
};

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);  // real profile from Firestore
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [viewAs, setViewAsState] = useState(readStoredViewAs); // role key or null

  // The profile is a LIVE subscription, not a one-shot read. The NavMenu
  // facility switcher (Phase 5.4) writes activeFacilityId on users/{uid};
  // every facility-scoped surface (board, daily list, messaging, alerts,
  // My Room) keys off user.activeFacilityId from this context — so the
  // context must re-emit when the doc changes or the whole app stays on the
  // old facility until the next sign-in. Same for admin edits to someone's
  // role/rooms: they now propagate without a re-login.
  useEffect(() => {
    let unsubProfile = null;
    const stopProfile = () => {
      if (unsubProfile) { unsubProfile(); unsubProfile = null; }
    };
    const failProfile = (err) => {
      // Most likely the security rules rejected the read/bootstrap write, or
      // the network failed. Surface it instead of leaving the user on a blank
      // screen with a silently-failed promise.
      console.error("Auth profile load failed:", err);
      setError(
        "We couldn't load your account profile. Contact an administrator " +
          "to have your access set up."
      );
      setUser(null);
      setLoading(false);
    };

    // Watchdog: if Firebase never delivers its first auth event, fall through
    // to the signed-out UI instead of holding the boot screen forever. A user
    // who can see the login form can still work; a user staring at a splash
    // cannot. Cleared the moment any real auth event arrives.
    let sawAuthEvent = false;
    const watchdog = setTimeout(() => {
      if (sawAuthEvent) return;
      console.warn(
        `Auth did not report within ${AUTH_WATCHDOG_MS}ms — showing signed-out UI.`
      );
      setUser(null);
      setLoading(false);
    }, AUTH_WATCHDOG_MS);

    const unsubAuth = onAuth((firebaseUser) => {
      sawAuthEvent = true;
      clearTimeout(watchdog);
      stopProfile();
      if (!firebaseUser) {
        setUser(null);
        clearViewAs(); // never carry a preview across sign-out
        setLoading(false);
        return;
      }

      // Bootstrap guard: the snapshot fires again after createUser succeeds,
      // so only provision once per signed-in session.
      let bootstrapped = false;
      unsubProfile = listenToUser(
        firebaseUser.uid,
        async (snap) => {
          try {
            if (snap.exists()) {
              setUser({ uid: firebaseUser.uid, ...snap.data() });
              setError(null);
            } else if (!bootstrapped) {
              // No profile doc yet → provision a least-privilege one. Admins
              // are granted only via the email allowlist in src/config/roles.js,
              // never by default. The security rules enforce the same
              // constraint, so a tampered client cannot self-promote.
              bootstrapped = true;
              // In sandbox mode this profile is written to the LOCAL store, not
              // to any hospital's database — `db` was swapped at boot.
              const defaultProfile = isSandboxBoot()
                ? buildSandboxProfile(firebaseUser)
                : buildDefaultProfile(firebaseUser);
              await createUser(firebaseUser.uid, defaultProfile);
              setUser(defaultProfile);
              setError(null);
            }
            setLoading(false);
          } catch (err) {
            failProfile(err);
          }
        },
        failProfile
      );
    });

    return () => { clearTimeout(watchdog); unsubAuth(); stopProfile(); };
  }, []);

  // ── Active AIC from today's Daily List ──────
  // The Daily Provider Assignment List for today names the AIC. When a list
  // exists, *that* person is the active AIC (the persistent isAIC user flag is
  // only the fallback when no list has been created). Reading it from the shared
  // daily_boards doc means selecting the AIC never has to write other users'
  // docs, which the security rules reserve for admins.
  const [todayAicId, setTodayAicId] = useState(null);
  const dayKey = useDayKey(); // LOCAL day, re-subscribes at midnight
  useEffect(() => {
    const facilityId = user?.activeFacilityId;
    if (!facilityId) { setTodayAicId(null); return; }
    const unsub = listenToDailyBoard(facilityId, dayKey, (snap) => {
      const data = snap.exists() ? snap.data() : null;
      setTodayAicId(data?.aicId ?? null);
    });
    return unsub;
  }, [user?.activeFacilityId, dayKey]);

  // ── Open the board they are actually working at ──────
  // Every profile is bootstrapped with the same activeFacilityId, and it only
  // ever changes if someone finds the NavMenu switcher — so a Tampa hire signs
  // in to Wesley Chapel's board, and a CRNA floated for the day sees the wrong
  // campus. Resolve it from today's assignment instead (domain/loginFacility).
  //
  // ONCE per sign-in, never per render: a manual switch mid-shift must survive.
  // Keyed by uid so switching accounts on a shared device re-resolves.
  const resolvedForUid = useRef(null);
  useEffect(() => {
    const uid = user?.uid;
    if (!uid || resolvedForUid.current === uid) return;
    // A Board Manager is a shared per-facility TV device with no personal
    // identity — its facility is a property of the device, not of a person.
    if (user.role === "boardmanager") { resolvedForUid.current = uid; return; }
    resolvedForUid.current = uid;

    let cancelled = false;
    (async () => {
      const allowed = Array.isArray(user.facilityIds) ? user.facilityIds.filter(Boolean) : [];
      const assignedFacilityIds = [];
      for (const fid of allowed) {
        try {
          const rows = await getAssignmentsOnce(fid, dayKey);
          // Staff list omitted deliberately: myRoomAssignments still matches on
          // uid, name and short code, and pulling the whole staff master on
          // every sign-in to catch the staff-id case is not worth the read.
          if (myRoomAssignments(rows, user, []).length) assignedFacilityIds.push(fid);
        } catch (err) {
          // A facility we cannot read is simply not a candidate. Never let this
          // block sign-in — the user still lands on their stored board.
          console.warn("Login facility: could not read assignments", fid, err);
        }
      }
      if (cancelled) return;
      const next = resolveLoginFacility({
        currentFacilityId: user.activeFacilityId,
        facilityIds: allowed,
        assignedFacilityIds,
        primaryFacilityId: user.primaryFacilityId || null,
      });
      if (!next.changed || !next.facilityId) return;
      try {
        // The live profile subscription re-emits, so every facility-scoped
        // surface follows without a reload.
        await updateUser(uid, { activeFacilityId: next.facilityId });
      } catch (err) {
        console.warn("Login facility: could not apply", next, err);
      }
    })();

    return () => { cancelled = true; };
  }, [user, dayKey]);

  // ── Presence heartbeat ──────────────────────
  // Firebase Auth knows a credential is valid, not that a person has the app
  // open — so "who is signed in right now" has to be observed. Stamp lastSeenAt
  // while the tab is VISIBLE only: a laptop left open on a locked screen is not
  // someone using the board, and beating in the background would both lie and
  // bill for it. See domain/presence for what this deliberately does not record.
  const heartbeatUid = user?.uid || null;
  useEffect(() => {
    if (!heartbeatUid) return undefined;
    let stopped = false;
    const beat = (login = false) => {
      if (stopped || document.visibilityState !== "visible") return;
      // Best-effort: presence must never surface an error to someone trying to
      // work. A missed beat only makes them look offline for a few minutes.
      touchPresence(heartbeatUid, { login }).catch(() => {});
    };
    beat(true); // sign-in / first foreground
    const timer = setInterval(() => beat(false), HEARTBEAT_MS);
    // Coming back to the tab should show them online immediately rather than
    // waiting out the remainder of an interval that ran while hidden.
    const onVisible = () => beat(false);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [heartbeatUid]);

  // ── Testing mode ("View as") ────────────────
  const realIsAdmin = user?.role === "admin";

  // Testing mode — admin-toggled, per device. While ON, the code-defined
  // testing accounts (src/config/testUsers.js) appear in every staff dropdown
  // and may be stepped into via "View as". While OFF they are fully hidden.
  const [testingModeState, setTestingModeState] = useState(readStoredTestingMode);
  const testingMode = realIsAdmin && testingModeState;
  const setTestingMode = (on) => {
    if (!realIsAdmin) return;
    setTestingModeState(!!on);
    try {
      if (on) localStorage.setItem(TESTING_MODE_KEY, "1");
      else localStorage.removeItem(TESTING_MODE_KEY);
    } catch { /* ignore */ }
    // Leaving testing mode also leaves any test-account perspective.
    if (!on && viewAsProfile(viewAs)?.isTestAccount) clearViewAs();
  };

  // Only a real admin may impersonate. The target is either:
  //   • a string  — a generic tier or test-account key (resolved via
  //                  viewAsProfile); test accounts also require testing mode ON.
  //   • an object — a specific REAL user from the directory ({ uid, name, role,
  //                  isAIC, subRole, currentRoomId, currentRoomName }), so the
  //                  preview shows that exact person's name, room, and rights.
  const setViewAs = (target) => {
    let value = null;
    if (typeof target === "string") {
      const profile = target ? viewAsProfile(target) : null;
      if (profile && !(profile.isTestAccount && !testingMode)) value = target;
    } else if (target && target.uid && target.role) {
      value = {
        key: "user:" + target.uid,
        uid: target.uid,
        name: target.name || "Unnamed user",
        role: target.role,
        isAIC: !!target.isAIC,
        subRole: target.subRole ?? null,
        currentRoomId: target.currentRoomId ?? null,
        currentRoomName: target.currentRoomName ?? null,
        isRealUser: true,
      };
    }
    if (!realIsAdmin || !value) {
      clearViewAs();
      return;
    }
    setViewAsState(value);
    try { sessionStorage.setItem(VIEW_AS_KEY, JSON.stringify(value)); } catch { /* ignore */ }
  };
  const clearViewAs = () => {
    setViewAsState(null);
    try { sessionStorage.removeItem(VIEW_AS_KEY); } catch { /* ignore */ }
  };

  // The patch is only honoured for a real admin with a valid target; test-account
  // patches are dropped the moment testing mode turns off. A string target maps
  // to a tier/test-user profile; an object target is already a real-user profile.
  const resolveViewAs = (v) =>
    !v ? null : (typeof v === "string" ? viewAsProfile(v) : v);
  const candidateViewAs = realIsAdmin ? resolveViewAs(viewAs) : null;
  const activeViewAs =
    candidateViewAs && candidateViewAs.isTestAccount && !testingMode
      ? null
      : candidateViewAs;
  const isPreviewing = !!activeViewAs;

  // The *effective* profile the rest of the UI reasons about: the real profile,
  // or the real profile shallow-patched with the previewed role. Test-account
  // previews also patch uid + name so "my rooms" views show that account's day.
  const effective = useMemo(() => {
    if (!user) return null;
    if (!activeViewAs) return user;
    return {
      ...user,
      role: activeViewAs.role,
      isAIC: activeViewAs.isAIC,
      subRole: activeViewAs.subRole ?? user.subRole,
      ...(activeViewAs.uid ? { uid: activeViewAs.uid, name: activeViewAs.name } : {}),
      // A real-user preview also carries that person's assigned room so the
      // board's "my room" view reflects exactly what they would see.
      ...(activeViewAs.isRealUser
        ? {
            currentRoomId: activeViewAs.currentRoomId ?? null,
            currentRoomName: activeViewAs.currentRoomName ?? null,
          }
        : {}),
    };
  }, [user, activeViewAs]);

  const value = useMemo(() => {
    // When previewing, the "View as" patch wins (the testing picker resolves the
    // board's AIC and sets isAIC explicitly). Otherwise, if today's Daily List
    // names an AIC, only that user is AIC; with no list, fall back to the
    // persistent isAIC flag from Setup → Users.
    const rosterDrivesAIC = !isPreviewing && todayAicId != null;
    const isAIC      = rosterDrivesAIC
      ? effective?.uid === todayAicId
      : effective?.isAIC === true;
    const isDoctor   = effective?.role === "anesthesiologist";
    const isProvider = effective?.role === "provider";
    const isAdmin    = effective?.role === "admin";
    const isObserver = effective?.role === "observer";
    // Scheduler: internal scheduling module only — no board/clinical access.
    const isScheduler = effective?.role === "scheduler";
    // Board Manager: shared per-facility TV-board device with AIC-level board
    // powers (no setup, no personal identity — assigns people from the roster).
    const isBoardManager = effective?.role === "boardmanager";

    // Tier key — mirror accessLevelOf(), but honour the roster-driven AIC above
    // so the displayed tier matches the live permissions.
    const accessLevel = !effective ? null
      : accessLevelOf({ role: effective.role, isAIC });

    // Permission matrix (derived once, consumed everywhere) ────────────────
    // Administrator: app/facility/user setup.
    const canManageSetup       = isAdmin;
    // AIC (or admin, or the facility's Board Manager device): open/close rooms
    // and approve breaks.
    const canManageRooms       = isAdmin || isAIC || isBoardManager;
    const canApproveBreaks     = isAdmin || isAIC || isBoardManager;
    // AIC, admin, board manager, and supervising doctors can create/change assignments…
    const canManageAssignments = isAdmin || isAIC || isDoctor || isBoardManager;
    // …but a supervising doctor's change must notify the AIC.
    const mustNotifyAIC        = isDoctor && !isAIC && !isAdmin;
    // Full board control (the AIC banner / drag-everything powers).
    const canEditBoard         = isAdmin || isAIC || isBoardManager;
    // Who may change a room's status (clinical staff only on their own room —
    // enforced again in the security rules and the room UI).
    const canChangeStatus      = isAdmin || isAIC || isDoctor || isProvider || isBoardManager;
    // Clinical staff request breaks; everyone clinical can raise alerts.
    const canRequestBreak      = isProvider;
    const canSendAlert         = !isObserver;

    return {
      user: effective,        // what the UI renders against
      realUser: user,         // the true signed-in profile
      loading, error,

      // tier
      accessLevel,
      accessLevelMeta: accessLevel ? ACCESS_LEVELS[accessLevel] : null,

      // role flags (effective)
      isAIC, isDoctor, isProvider, isAdmin, isObserver, isScheduler, isBoardManager,
      // Scheduling module access: admins + schedulers.
      canManageSchedule: isAdmin || isScheduler,
      // Daily Assignments page (roster building) — Administrators and
      // Schedulers only. Doctors/AIC work the OR Board; the daily list is a
      // scheduling surface (mirrored by the Firestore rules).
      canManageDailyList: isAdmin || isScheduler,

      // permissions
      canManageSetup, canManageRooms, canApproveBreaks,
      canManageAssignments, mustNotifyAIC, canEditBoard,
      canChangeStatus, canRequestBreak, canSendAlert,

      // testing mode
      realIsAdmin, isPreviewing, viewAs: activeViewAs?.key || null,
      // The previewed person's name (real user or test account); null for a
      // generic tier preview. Drives the banner's "Previewing as Name (Role)".
      previewName: activeViewAs?.name || null,
      setViewAs, clearViewAs,
      testingMode, setTestingMode,
    };
  }, [effective, user, loading, error, realIsAdmin, isPreviewing, activeViewAs, todayAicId, testingMode]);

  // While auth resolves, render a VISIBLE placeholder rather than nothing.
  //
  // This used to be `{!loading && children}`, which renders an empty document
  // whenever `loading` never clears — and `loading` only clears inside the
  // Firebase auth callback. So any failure to reach Firebase produced a blank
  // white screen with no way to tell a broken app from a slow network. The
  // first iOS TestFlight build failed exactly that way.
  //
  // After BOOT_STALL_MS the copy changes to say something is actually wrong,
  // so a stuck boot reports itself instead of looking like a hung splash.
  if (loading) return <BootScreen />;

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

const BOOT_STALL_MS = 12000;

function BootScreen() {
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setStalled(true), BOOT_STALL_MS);
    return () => clearTimeout(t);
  }, []);
  return (
    <main
      role="status"
      aria-live="polite"
      style={{
        position: "fixed", inset: 0, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", gap: 12, padding: 24,
        textAlign: "center", fontFamily: "system-ui, sans-serif",
      }}
    >
      <div style={{ fontSize: 16, fontWeight: 600 }}>Anesthesia Team Control</div>
      {stalled ? (
        <div style={{ fontSize: 14, maxWidth: 320, lineHeight: 1.5 }}>
          Still trying to reach the server. Check your connection — the app will
          continue on its own once it connects.
        </div>
      ) : (
        <div style={{ fontSize: 14 }}>Signing you in…</div>
      )}
    </main>
  );
}

export const useAuth = () => useContext(AuthContext);
